import axios from 'axios';
import {
  IngestPayload,
  CachedItem,
  DocumentsRequest,
  ListFilesResponse,
  TenantConfig,
  AdapterTenantConfig,
  SystemPromptResponse,
  ObservationSuggestionModel,
  ObservationSuggestionsResponse,
  HighlightSuggestionsResponse,
  ObservationsSuggestionsUpsertResponse,
  GeneralResponse,
  UpdateHighlightSuggestionsResponse,
  HighlightSuggestionUpdateRequest,
  TagConfig,
  Tag,
  TagValuesListResponse,
  ConfigMigrationStatus,
  PrepareDocumentsResponse,
  DocumentTag,
  TagValueGroupsListResponse,
  TagValueGroupsUpsertResponse,
  TagValueGroupsDeleteResponse,
  TagValueGroupUpsertItem,
  PartImageIngestResponse,
  ListPartsResponse,
  DeletePartsResponse
} from '../components/copilot setup/models';
import { CustomPromptScope } from '../components/copilot setup/types';


// Prefixes
const scriptApiPrefix       = "/scripts";
const taskApiPrefix         = "/task";
const filesApiPrefix        = "/files";
const adminApiPrefix        = "/admin";
const tagApiPrefix          = "/tags";
const ingestApiPrefix       = "/ingest";
const customPromptApiPrefix = "/custom-prompt";
const dataAdapterPrefix     = "/data-adapter";
const offlineApiPrefix      = "/offline";
const partsApiPrefix        = "/parts";


// URIs
const runScript                = scriptApiPrefix + "/run_script";
const migrateFilename          = scriptApiPrefix + "/migrate_filename";
const abortTasks               = taskApiPrefix + "/abort";

const uploadUrls               = filesApiPrefix + "/{tenant_id}/upload_urls";
const migrationStatus          = filesApiPrefix + "/{tenant_id}/migration_status?task_id={task_id}";

const tenantInfo               = adminApiPrefix + "/tenant/{tenant_id}/info";
const tenantConfig             = adminApiPrefix + "/tenant/{tenant_id}/config";
const configMigrationStatus    = adminApiPrefix + "/tenant/{tenant_id}/config/migration_status";
const startConfigMigration     = adminApiPrefix + "/tenant/{tenant_id}/config/migrate";
const abortConfigMigration     = adminApiPrefix + "/tenant/{tenant_id}/config/abort";
const resumeConfigMigration    = adminApiPrefix + "/tenant/{tenant_id}/config/resume";
const swapConfigMigration      = adminApiPrefix + "/tenant/{tenant_id}/config/swap";
const clearConfigMigration     = adminApiPrefix + "/tenant/{tenant_id}/config/clear_migration_data";
const copyNonMigrated          = adminApiPrefix + "/tenant/{tenant_id}/config/copy_non_migrated";

const getTagConfigs            = tagApiPrefix + "/configs/list";
const upsertTagConfigs         = tagApiPrefix + "/configs/upsert";
const getTagValues             = tagApiPrefix + "/values/list";
const upsertTagValues          = tagApiPrefix + "/values/upsert";
const updateDocumentTags       = tagApiPrefix + "/document_tags/update";
const updateDocumentTagsWhere  = tagApiPrefix + "/document_tags/update_where";
const updateUsersPersona       = tagApiPrefix + "/persona/update_users";
const listTagValueGroups       = tagApiPrefix + "/groups/list";
const upsertTagValueGroups     = tagApiPrefix + "/groups/upsert";
const deleteTagValueGroups     = tagApiPrefix + "/groups/delete";


const getIngestModels          = ingestApiPrefix + "/models/{tenant_id}/list";
const getDocuments             = ingestApiPrefix + "/documents/{tenant_id}/list";
const addDocuments             = ingestApiPrefix + "/documents/{tenant_id}/add";
const deleteDocuments          = ingestApiPrefix + "/documents/{tenant_id}/delete";
const syncStorage              = ingestApiPrefix + "/documents/sync_storage/run";
const syncStorageTask          = ingestApiPrefix + "/documents/sync_storage/task";
const ingest                   = ingestApiPrefix + "/ingest/{tenant_id}";
const ingestPartImages         = ingestApiPrefix + "/part_images";

const listParts                = partsApiPrefix + "/list";
const deleteParts              = partsApiPrefix + "/delete";
const deletePartImages         = partsApiPrefix + "/images/delete";
const getCacheItems            = ingestApiPrefix + "/cache/{tenant_id}/{model_name}/list";
const editCacheItems           = ingestApiPrefix + "/cache/{tenant_id}/{model_name}/edit";
const deleteCacheItems         = ingestApiPrefix + "/cache/{tenant_id}/{model_name}/delete";

const getDeleteCustomPromptUrl = customPromptApiPrefix + "/?scope={scope}&tenant_id={tenant_id}&model={model}"
const getAllCustomPromptsUrl   = customPromptApiPrefix + "/prompts/{tenant_id}"
const upsertCustomPromptUrl    = customPromptApiPrefix + "/"
const getSystemCustomPromptUrl = customPromptApiPrefix + "/system-prompt?tenant_id={tenant_id}&split_ending={split_ending}"
const validateCustomPromptUrl  = customPromptApiPrefix + "/validate"
const testCustomPromptUrl      = customPromptApiPrefix + "/test"
const getAllConfigs            = dataAdapterPrefix + "/get_all_configs"
const getConfigIDByCustomer    = dataAdapterPrefix + "/get_tenant_config?tenant_id={tenant_id}"
const updateCustomerConfig     = dataAdapterPrefix + "/update_tenant_config?tenant_id={tenant_id}&config_id={config_id}"

const getObservationSuggestions    = adminApiPrefix + "/suggestions/observation/{tenant_id}/{model_name}/list";
const deleteObservationSuggestions = adminApiPrefix + "/suggestions/observation/{tenant_id}/{model_name}/delete";
const upsertObservationSuggestions = adminApiPrefix + "/suggestions/observation/{tenant_id}/{model_name}/upsert";
const allModelsObservationSuggestions = adminApiPrefix + "/suggestions/all-models/observation";
const getHighlightSuggestions      = adminApiPrefix + "/suggestions/highlight/{tenant_id}/{model_name}/list";
const updateHighlightSuggestions   = adminApiPrefix + "/suggestions/highlight/{tenant_id}/{model_name}/update";
const deleteHighlightSuggestions   = adminApiPrefix + "/suggestions/highlight/{tenant_id}/{model_name}/delete";

const prepareOfflineData = offlineApiPrefix + "/documents/prepare";
const getDocumentPrepStatus = offlineApiPrefix + "/documents/prepare_in_progress";
class Backend {

    baseUrl: string = 'undefined_base_url'
    token: () => string | undefined

    constructor(baseUrl: string, getToken: () => string | undefined) {
        this.baseUrl = baseUrl
        this.token = getToken
    }

  private async post(url: string, headers: any, data?: any) {
    return await axios({
      method: "post",
      url: this.baseUrl + url,
      headers: headers,
      data: data
    });
  }

  private async get(url: string, headers: any) {
    return await axios({
      method: "get",
      url: this.baseUrl + url,
      headers: headers
    });
  }

  private async delete(url: string, headers: any, data?: any) {
    return await axios({
      method: "delete",
      url: this.baseUrl + url,
      headers: headers,
      data: data
    });
  }

  private getCommonHeaders() {
    return {
      "Authorization" : "Bearer " + this.token()
    };
  }

  public async getIngestModels(tenant_id: string){
    const response = await this.post(getIngestModels.replace('{tenant_id}', tenant_id), this.getCommonHeaders())
    return response.data
  }

  public async getUploadUrls(tenant_id: string, filenames: string[], requested_by_email: string){
    const response = await this.post(uploadUrls.replace('{tenant_id}', tenant_id) + `?requested_by=${requested_by_email}`, this.getCommonHeaders(), filenames)
    return response.data
  }

  public async getFiles(tenant_id: string, offset?: number, limit: number = 10000){
    const req = {
        limit,
        offset: offset || 0,
    };
    const response = await this.post(getDocuments.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
    return response.data as ListFilesResponse
  }

  public async addFiles(tenant_id: string, filenames: string[], requested_by_email: string){
    const req: DocumentsRequest = {
      document_names: filenames,
      reset_existing_status: true,
      requested_by_email
    }
    return this.post(addDocuments.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
  }

  public async deleteFiles(tenant_id: string, document_ids: string[], requested_by_email: string){
    const req: DocumentsRequest = {
      document_ids,
      requested_by_email
    }
    return this.post(deleteDocuments.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
  }

  public async postSyncStorage(tenant_id: string) {
    const req = {tenant_id}
    return await this.post(syncStorage, this.getCommonHeaders(), req)
  }

  public async getSyncStorageTask(tenant_id: string) {
    const req = {tenant_id}
    return await this.post(syncStorageTask, this.getCommonHeaders(), req)
  }

  public async ingest(tenant_id: string, ingest_payload: IngestPayload){
    const response = await this.post(ingest.replace('{tenant_id}', encodeURIComponent(tenant_id)), this.getCommonHeaders(), ingest_payload)
    return response.status === 200 ? {"status": 'Success'} : {"status": 'Error'}
  }

  // Tenant comes from the X-Tenant-Id header here, not the path - that's how the endpoint resolves it.
  public async ingestPartImages(tenant_id: string, reingest_existing: boolean){
    const response = await this.post(
      ingestPartImages + `?reingest_existing=${reingest_existing}`,
      { ...this.getCommonHeaders(), "X-Tenant-Id": tenant_id }
    )
    return response.data as PartImageIngestResponse
  }

  // /parts routes below also resolve the tenant from the header - they have no /{tenant_id}/ variant.
  private tenantHeaders(tenant_id: string) {
    return { ...this.getCommonHeaders(), "X-Tenant-Id": tenant_id }
  }

  public async listParts(tenant_id: string, limit: number, offset: number, has_images?: boolean){
    const req = { limit, offset, order_by: 'created_at', is_descending: false, ...(has_images !== undefined && { has_images }) }
    const response = await this.post(listParts, this.tenantHeaders(tenant_id), req)
    return response.data as ListPartsResponse
  }

  public async deleteParts(tenant_id: string, ids: string[]){
    const response = await this.post(deleteParts, this.tenantHeaders(tenant_id), { ids })
    return response.data as DeletePartsResponse
  }

  public async deletePartImages(tenant_id: string, rowids: string[]){
    await this.post(deletePartImages, this.tenantHeaders(tenant_id), { rowids })
  }

  public async saveTenantInfo(tenant_id: string, sf_org_id?: string, tenant_type?: string){
    const req = {
      ...(sf_org_id !== null && sf_org_id !== undefined && { sf_org_id }),
      ...(tenant_type !== null && tenant_type !== undefined && { tenant_type }),
    };

    const response = await this.post(tenantInfo.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
    if (response?.data){
      return {
        sf_org_id: response.data.sf_org_id,
        isProd: response.data.tenant_type === "Prod"
      }
    }
    return response.data
  }

  public async getTenantConfig(tenant_id: string){
    const response = await this.get(tenantConfig.replace('{tenant_id}', tenant_id), this.getCommonHeaders())
    return response.data
  }

  public async getConfigMigrationStatus(tenant_id: string): Promise<ConfigMigrationStatus> {
    const response = await this.get(configMigrationStatus.replace('{tenant_id}', tenant_id), this.getCommonHeaders())
    return response.data
  }

  public async startConfigMigration(tenant_id: string, embedding_model: string, pdf_read_method: string, user_email: string): Promise<any> {
    const req = { embedding_model, pdf_read_method, user_email }
    const response = await this.post(startConfigMigration.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
    return response.data
  }

  public async abortConfigMigration(tenant_id: string, user_email: string): Promise<any> {
    const req = { user_email }
    const response = await this.post(abortConfigMigration.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
    return response.data
  }

  public async copyNonMigrated(tenant_id: string): Promise<any> {
    const response = await this.post(copyNonMigrated.replace('{tenant_id}', tenant_id), this.getCommonHeaders())
    return response.data
  }

  public async resumeConfigMigration(tenant_id: string, user_email: string): Promise<any> {
    const req = { user_email }
    const response = await this.post(resumeConfigMigration.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
    return response.data
  }

  public async swapConfigMigration(tenant_id: string, user_email: string): Promise<any> {
    const req = { user_email }
    const response = await this.post(swapConfigMigration.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
    return response.data
  }

  public async clearConfigMigration(tenant_id: string, user_email: string): Promise<any> {
    const req = { user_email }
    const response = await this.post(clearConfigMigration.replace('{tenant_id}', tenant_id), this.getCommonHeaders(), req)
    return response.data
  }

  public async saveTenantConfig(tenant_id: string, tenant_config: TenantConfig, user_email: string){
    const response = await this.post(
      tenantConfig.replace('{tenant_id}', tenant_id) + `?user_email=${user_email}`,
      this.getCommonHeaders(),
      tenant_config)
    return response.data
  }

  public async getCacheItems(tenant_id: string, model_name: string){
    const req = {
      "page_size": 500,
      "page_no": 1
    }
    const response = await this.post(getCacheItems.replace('{tenant_id}', tenant_id).replace('{model_name}', model_name), this.getCommonHeaders(), req)
    return response.data.items as CachedItem[]
  }

  public async editCacheItems(tenant_id: string, model_name: string, items: CachedItem[]){
    const req = {items}
    return this.post(editCacheItems.replace('{tenant_id}', tenant_id).replace('{model_name}', model_name), this.getCommonHeaders(), req)
  }

  public async deleteCacheItems(tenant_id: string, model_name: string, item_ids: string[]){
    const req = {items: item_ids}
    return this.post(deleteCacheItems.replace('{tenant_id}', tenant_id).replace('{model_name}', model_name), this.getCommonHeaders(), req)
  }

  public async getSystemPrompt(tenant_id: string){
    const response = await this.get(getSystemCustomPromptUrl.replace("{tenant_id}", tenant_id).replace("{split_ending}", "true"), this.getCommonHeaders());
    return response.data as SystemPromptResponse;
  }

  public async getCustomPrompt(scope: CustomPromptScope, tenant_id: string, model: string = ''){
    const response = await this.get(getDeleteCustomPromptUrl.replace("{scope}", scope).replace("{tenant_id}", tenant_id).replace("{model}", model), this.getCommonHeaders())
    return response.data
  }

  public async getAllPrompts(tenant_id: string){
    const response = await this.get(getAllCustomPromptsUrl.replace("{tenant_id}", encodeURIComponent(tenant_id)), this.getCommonHeaders())
    return response.data
  }

  public async upsertCustomPrompt(scope: CustomPromptScope,tenant_id: string, prompt: string, prompt_title: string, model?: string){
    let req = {
      scope: scope,
      tenant_id: tenant_id,
      prompt: prompt,
      prompt_title,
      model
    }

    await this.post(upsertCustomPromptUrl, this.getCommonHeaders(), req)
  }

  public async validateCustomPrompt(tenant_id?: string, customPrompt?: string){
    const req = {
      tenant_id: tenant_id,
      custom_prompt: customPrompt
    }
    const response = await this.post(validateCustomPromptUrl, this.getCommonHeaders(), req)
    return response.data
  }

  public async deleteCustomPrompt(scope: CustomPromptScope, tenant_id: string, model: string = '') {
    await this.delete(customPromptApiPrefix + `/?scope=${scope}&tenant_id=${tenant_id}&model=${model}`, this.getCommonHeaders())
  }

  public async testCustomPrompt(tenant_id: string, model: string, prompt: string, question: string) {
    const req = {
      tenant_id: tenant_id,
      model: model,
      prompt: prompt,
      question: question
    }
    const response = await this.post(testCustomPromptUrl, this.getCommonHeaders(), req)
    return response.data
  }
  public async getAllConfigs(){
    const response = await this.get(getAllConfigs, this.getCommonHeaders())
    return response.data as AdapterTenantConfig[]
  }

  public async getConfigIDByCustomer(tenant_id: string){
    const response = await this.get(getConfigIDByCustomer.replace('{tenant_id}', tenant_id), this.getCommonHeaders())
    return response.data
  }

  public async updateCustomerAdapterConfig(tenant_id: string, config_id: string){
    const response = await this.post(updateCustomerConfig.replace('{tenant_id}', tenant_id).replace('{config_id}', config_id), this.getCommonHeaders())
    return response.data
  }

  public async getStorageMigrationStatus(tenant_id: string, task_id?: string){

    const response = await this.post(migrationStatus.replace('{tenant_id}', tenant_id).replace('{task_id}', task_id ?? ''), this.getCommonHeaders())
    return response.data
  }

  public async postRunScript(tenant_id: string, script_name: string, task_data: any = {}) {
    const req = {
      script_name,
      tenant_id,
      task_data
    }
    const response = await this.post(runScript, this.getCommonHeaders(), req)
    return response.data
  }

  public async migrateFilename(tenant_id: string, filename: string, migrate_to_id: string) {
    const req = {
      tenant_id,
      filename,
      migrate_to_id
    }
    const response = await this.post(migrateFilename, this.getCommonHeaders(), req)
    return response.data
  }

  public async postAbortTasks(task_ids: string[]) {
    const req = {
      task_ids
    }
    const response = await this.post(abortTasks, this.getCommonHeaders(), req)
    return response.data
  }

  // Add methods for observation suggestions
  private observationSuggestionsRoute(tenant_id: string, model_name: string | null, action: 'list' | 'upsert' | 'delete') {
    if (model_name === null) {
      return { url: `${allModelsObservationSuggestions}/${action}`, headers: this.tenantHeaders(tenant_id) }
    }
    const template = { list: getObservationSuggestions, upsert: upsertObservationSuggestions, delete: deleteObservationSuggestions }[action]
    return { url: template.replace('{tenant_id}', tenant_id).replace('{model_name}', model_name), headers: this.getCommonHeaders() }
  }

  public async getObservationSuggestions(tenant_id: string, model_name: string | null): Promise<ObservationSuggestionsResponse> {
    const { url, headers } = this.observationSuggestionsRoute(tenant_id, model_name, 'list');
    const response = await this.get(url, headers);
    return response.data;
  }

  public async upsertObservationSuggestions(tenant_id: string, model_name: string | null, createdItems: Array<ObservationSuggestionModel>, updatedItems: Array<ObservationSuggestionModel>): Promise<ObservationsSuggestionsUpsertResponse> {
    const { url, headers } = this.observationSuggestionsRoute(tenant_id, model_name, 'upsert');
    const response = await this.post(url, headers, { add: createdItems, update: updatedItems });
    return response.data;
  }

  public async deleteObservationSuggestions(tenant_id: string, model_name: string | null, rowids: string[]): Promise<GeneralResponse> {
    const { url, headers } = this.observationSuggestionsRoute(tenant_id, model_name, 'delete');
    const response = await this.post(url, headers, { rowids });
    return response.data;
  }

  // Add methods for highlight suggestions
  public async getHighlightSuggestions(tenant_id: string, model_name: string): Promise<HighlightSuggestionsResponse> {
    const response = await this.get(getHighlightSuggestions.replace('{tenant_id}', tenant_id).replace('{model_name}', model_name), this.getCommonHeaders());
    return response.data;
  }

  public async updateHighlightSuggestions(tenant_id: string, model_name: string, updateRequest: HighlightSuggestionUpdateRequest): Promise<UpdateHighlightSuggestionsResponse> {
    const response = await this.post(updateHighlightSuggestions.replace('{tenant_id}', tenant_id).replace('{model_name}', model_name), this.getCommonHeaders(), updateRequest);
    return response.data;
  }

  public async deleteHighlightSuggestions(tenant_id: string, model_name: string, rowids: string[]): Promise<GeneralResponse> {
    const req = {
      rowids
    };
    const response = await this.post(deleteHighlightSuggestions.replace('{tenant_id}', tenant_id).replace('{model_name}', model_name), this.getCommonHeaders(), req);
    return response.data;
  }

  public async getTagConfigs(tenant_id: string): Promise<TagConfig[]> {
    const req = {
      tenant_id
    };
    const response = await this.post(getTagConfigs, this.getCommonHeaders(), req);
    return response.data;
  }

  public async upsertTagConfigs(tenant_id: string, tag_configs: TagConfig[]): Promise<any> {
    const req = {
      tenant_id,
      tag_configs
    };
    const response = await this.post(upsertTagConfigs, this.getCommonHeaders(), req);
    return response.data;
  }

  public async getTagValues(
    tenant_id: string,
    limit: number,
    offset: number,
    options?: { tag_index?: number; selected_tags?: Tag[]; is_active?: boolean }
  ): Promise<TagValuesListResponse> {
    const req = {
      tenant_id,
      limit,
      offset,
      ...(options?.tag_index !== undefined && { tag_index: options.tag_index }),
      ...(options?.selected_tags !== undefined && { selected_tags: options.selected_tags }),
      ...(options?.is_active !== undefined && { is_active: options.is_active }),
    };
    const response = await this.post(getTagValues, this.getCommonHeaders(), req);
    return response.data;
  }

  public async upsertTagValues(tenant_id: string, tag_values: Tag[]): Promise<any> {
    const req = {
      tenant_id,
      tag_values
    };
    const response = await this.post(upsertTagValues, this.getCommonHeaders(), req);
    return response.data;
  }

  public async getTagValueGroups(tenant_id: string): Promise<TagValueGroupsListResponse> {
    const req = { tenant_id };
    const response = await this.post(listTagValueGroups, this.getCommonHeaders(), req);
    return response.data;
  }

  public async upsertTagValueGroups(
    tenant_id: string,
    groups: TagValueGroupUpsertItem[],
    replace_existing: boolean = false,
  ): Promise<TagValueGroupsUpsertResponse> {
    const req = { tenant_id, groups, replace_existing };
    const response = await this.post(upsertTagValueGroups, this.getCommonHeaders(), req);
    return response.data;
  }

  public async upsertTagValueGroupsChunked(
    tenant_id: string,
    groups: TagValueGroupUpsertItem[],
    replace_existing: boolean = false,
    chunkSize: number = 10000,
  ): Promise<TagValueGroupsUpsertResponse> {
    let totalCreated = 0;
    let totalUpdated = 0;
    let anyFailed = false;
    let allInvalidTvids: string[] = [];
    let allInvalidGids: string[] = [];

    for (let i = 0; i < groups.length; i += chunkSize) {
      const chunk = groups.slice(i, i + chunkSize);
      const isFirst = i === 0;
      const response = await this.upsertTagValueGroups(
        tenant_id,
        chunk,
        isFirst ? replace_existing : false,
      );
      if (!response.success) anyFailed = true;
      totalCreated += response.groups_created ?? 0;
      totalUpdated += response.groups_updated ?? 0;
      if (response.invalid_tag_value_ids) allInvalidTvids = allInvalidTvids.concat(response.invalid_tag_value_ids);
      if (response.invalid_group_ids) allInvalidGids = allInvalidGids.concat(response.invalid_group_ids);
    }

    return {
      success: !anyFailed,
      groups_created: totalCreated,
      groups_updated: totalUpdated,
      invalid_tag_value_ids: allInvalidTvids.length ? allInvalidTvids : undefined,
      invalid_group_ids: allInvalidGids.length ? allInvalidGids : undefined,
    };
  }

  public async deleteTagValueGroups(tenant_id: string, group_ids: string[]): Promise<TagValueGroupsDeleteResponse> {
    const req = { tenant_id, group_ids };
    const response = await this.post(deleteTagValueGroups, this.getCommonHeaders(), req);
    return response.data;
  }

  public async updateDocumentTags(tenant_id: string, tags_to_remove?: DocumentTag[], tags_to_add?: DocumentTag[]): Promise<any> {
    const req = {
      tenant_id,
      tags_to_remove,
      tags_to_add
    };
    const response = await this.post(updateDocumentTags, this.getCommonHeaders(), req);
    return response.data;
  }

  public async updateDocumentTagsWhere(tenant_id: string, add_tag_value_ids:string[], where_tag_value_id:string): Promise<any> {
    const req = {
      tenant_id,
      add_tag_value_ids,
      where_tag_value_id
    };
    const response = await this.post(updateDocumentTagsWhere, this.getCommonHeaders(), req);
    return response.data;
  }

  public async updateUsersPersona(tenant_id: string, update_to_persona_id: string, filter_by_persona_id: string): Promise<any> {
    const req = {
      tenant_id,
      update_to_persona_id,
      filter_by_persona_id
    };
    const response = await this.post(updateUsersPersona, this.getCommonHeaders(), req);
    return response.data;
  }

  public async prepareOfflineData(tenant_id: string, document_ids: string[]): Promise<PrepareDocumentsResponse> {
    const req = {
      tenant_id,
      document_ids
    };
    const response = await this.post(prepareOfflineData, this.getCommonHeaders(), req);
    return response.data;
  }

  public async getOfflinePrepStatus(tenant_id: string): Promise<PrepareDocumentsResponse> {
    const req = {
        tenant_id,
        document_ids: [],
    };
    const response = await this.post(getDocumentPrepStatus, this.getCommonHeaders(), req);
    return response.data;
  }
}

export default Backend;